import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { Select, SelectProps } from "./base";

export interface ControlledSelectProps extends Omit<
  SelectProps,
  "value" | "onChange" | "onBlur" | "error" | "required" | "inputRef"
> {
  /** Field name registered on the surrounding react-hook-form context. */
  name: string;
  /** Read the error from a different field than `name` (e.g. a grouped error). */
  errorName?: string;
  rules?: {
    required?: { value: boolean; message: string };
    validate?: (
      val: string,
    ) => string | boolean | undefined | Promise<string | boolean | undefined>;
  };
}

/**
 * react-hook-form–bound {@link Select}: the form owns the value and validation
 * via `name`/`rules`. Reach for this inside a form; use the plain controlled
 * `Select` when a non-form parent owns the value, or `UncontrolledSelect` when
 * the field is self-contained.
 */
export function ControlledSelect({
  name,
  errorName = "",
  rules = {},
  disabled = false,
  ...rest
}: ControlledSelectProps) {
  const { control, getFieldState } = useFormContext();
  const { field, fieldState } = useController({ name, control, rules });
  const required = rules.required?.value === true;

  const { error } = useMemo(
    () => getFieldState(errorName ? errorName : name),
    [getFieldState, errorName, name, fieldState.error],
  );

  return (
    <Select
      {...rest}
      name={field.name}
      value={field.value ?? ""}
      onChange={field.onChange}
      onBlur={field.onBlur}
      inputRef={field.ref}
      disabled={field.disabled || disabled}
      required={required}
      error={error?.message}
    />
  );
}

export default ControlledSelect;
